import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Search as SearchIcon,
} from '@mui/icons-material_modern'
import { Button, Stack, Typography, IconButton, Badge } from '@mui/material_modern'
import { useGridApiRef } from '@mui/x-data-grid-pro_modern'
import { randomId } from '@mui/x-data-grid-generator_modern'

// state
import store from 'state/store'
import { showFormModal } from 'state/ui'

// components
import BasicDataGridV2 from 'components/generic/datagrid/BasicDataGridV2'
import { useCustomDataGridStyles } from 'components/containers/load-board/utils/useCustomStyles'

// utils
import FORM_MODALS from 'utils/ui/modals'
import { DISPATCH_TICKET_TYPES } from 'utils/constants'
import { setAssets } from 'state/dispatch'
import AssetImagesLightbox from 'components/containers/load-board/components/AssetImagesLightbox'

const AssetList = ({
  methods,
  type,
  disabled,
  ticketIsResolved,
  fromLocationId,
  setHasEditedAssets,
  assetShouldDirty,
  setAssetShouldDirty,
  ticketStatus,
}) => {
  const { setValue } = methods
  const assetListApiRef = useGridApiRef()
  const dispatch = useDispatch()
  const classes = useCustomDataGridStyles({})
  const { pickupAssets, dropoffAssets, pickAndReturnAssets, deliveryAssets, liveLoadAssets } = useSelector(
    state => state.dispatch,
  )
  const [isMounted, setIsMounted] = useState(false)
  const [lightboxAsset, setLightboxAsset] = useState(null)
  let assets
  if (type === DISPATCH_TICKET_TYPES.PICK_UP.id) {
    assets = pickupAssets
  } else if (type === DISPATCH_TICKET_TYPES.DROP_OFF.id) {
    assets = dropoffAssets
  } else if (type === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) {
    assets = pickAndReturnAssets
  } else if (type === DISPATCH_TICKET_TYPES.DELIVERY.id) {
    assets = deliveryAssets
  } else if (type === DISPATCH_TICKET_TYPES.LIVE_LOAD.id) {
    assets = liveLoadAssets
  }

  useEffect(() => {
    if (type === DISPATCH_TICKET_TYPES.PICK_UP.id) {
      setValue('pickupAssets', pickupAssets, { shouldDirty: assetShouldDirty })
    } else if (type === DISPATCH_TICKET_TYPES.DROP_OFF.id) {
      setValue('dropoffAssets', dropoffAssets, { shouldDirty: assetShouldDirty })
    } else if (type === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) {
      setValue('pickAndReturnAssets', pickAndReturnAssets, { shouldDirty: assetShouldDirty })
    } else if (type === DISPATCH_TICKET_TYPES.DELIVERY.id) {
      setValue('deliveryAssets', deliveryAssets, { shouldDirty: assetShouldDirty })
    } else if (type === DISPATCH_TICKET_TYPES.LIVE_LOAD.id) {
      setValue('liveLoadAssets', liveLoadAssets, { shouldDirty: assetShouldDirty })
    }
  }, [assets])

  const selectedLocation = methods.watch('location')
  const handleAddContainer = () => {
    setAssetShouldDirty(true)
    store.dispatch(
      showFormModal({
        formModalName: FORM_MODALS.ASSET_SELECTION_BLADE,
        formModalProps: { type, selectedLocation, fromLocationId, ticketStatus },
      }),
    )
  }

  const handleDelete = id => {
    setHasEditedAssets(true)
    const newAssetList = assets.filter(asset => asset.id !== id)
    dispatch(setAssets({ ticketType: type, assets: newAssetList }))
  }

  useEffect(() => {
    if (isMounted && assets?.length === 0 && assetListApiRef?.current?.getRowsCount?.() === 0) {
      assetListApiRef.current?.updateRows([{ id: randomId(), isNew: true }])
    }
  }, [isMounted, assets, assetListApiRef])

  const assetColumns = useMemo(
    () => [
      {
        field: 'assetName',
        headerName: 'Container ID',
        type: 'string',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        renderCell: ({ row }) => {
          const assetName = row?.asset?.assetName || row?.assetName
          if (assetName) return <Typography>{assetName}</Typography>
          return (
            <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
              <SearchIcon color={disabled ? 'disabled' : 'success'} />
              <Button variant="text" onClick={handleAddContainer} disabled={disabled}>
                SELECT
              </Button>
            </Stack>
          )
        },
      },
      {
        field: 'type',
        headerName: 'Container Type',
        type: 'string',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        renderCell: ({ row }) => {
          const containerType = row?.asset?.type || row?.type
          if (containerType) return <Typography>{containerType}</Typography>
          return <Stack flexDirection="row" alignItems="center" justifyContent="space-between" />
        },
      },
      {
        field: 'actions',
        headerName: '',
        renderCell: ({ row }) => (
          <div>
            <IconButton onClick={() => setLightboxAsset(row)} disabled={!row?.images?.length}>
              <Badge badgeContent={row?.images?.length} color="primary">
                <ImageIcon />
              </Badge>
            </IconButton>

            <IconButton color="error" onClick={() => handleDelete(row.id)} disabled={disabled}>
              <DeleteIcon />
            </IconButton>
          </div>
        ),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
      },
    ],
    [assets, handleDelete, disabled],
  )

  return (
    <Stack sx={{ width: '100%', p: 1 }}>
      <Typography variant="body1">{DISPATCH_TICKET_TYPES[type]?.name} Assets</Typography>
      <BasicDataGridV2
        className={classes.root}
        rows={assets}
        columns={assetColumns}
        apiRef={assetListApiRef}
        editMode="row"
        disableColumnMenu
        hideFooter
        onMounted={() => setIsMounted(true)}
        components={{
          LoadingOverlay: () => null,
        }}
      />
      {!ticketIsResolved && (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddContainer} disabled={disabled}>
            Add Container
          </Button>
        </Stack>
      )}
      <AssetImagesLightbox lightboxAsset={lightboxAsset} onClose={() => setLightboxAsset(null)} />
    </Stack>
  )
}

AssetList.propTypes = {
  methods: PropTypes.object,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  ticketIsResolved: PropTypes.bool,
  fromLocationId: PropTypes.string,
  setHasEditedAssets: PropTypes.func.isRequired,
  assetShouldDirty: PropTypes.bool,
  setAssetShouldDirty: PropTypes.func,
}

AssetList.defaultProps = {
  methods: {},
  type: DISPATCH_TICKET_TYPES.PICK_UP.id,
  disabled: true,
  ticketIsResolved: true,
  fromLocationId: '',
  assetShouldDirty: false,
  setAssetShouldDirty: () => {},
}

export default AssetList
